import {
  authApi,
  setCurrentOrganizationId,
  userApi,
  type CurrentUser,
} from '@shared/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { setUnauthorizedHandler } from '@/lib/api-setup';

type SessionContextValue = {
  user: CurrentUser | null;
  isLoading: boolean;
  /** Distinguishes "not signed in" from "we have not checked yet". */
  isResolved: boolean;
  signOut: () => Promise<void>;
  switchOrganization: (organizationId: string) => void;
  refresh: () => Promise<unknown>;
};

const SessionContext = React.createContext<SessionContextValue | null>(null);

export const USER_QUERY_KEY = ['user'] as const;

export const SessionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    isFetched,
    refetch,
  } = useQuery({
    queryKey: USER_QUERY_KEY,
    queryFn: userApi.getCurrentUser,
    /**
     * A 401 is the normal answer for a signed-out visitor, so it resolves to
     * null rather than surfacing as an error state the router has to interpret.
     */
    retry: false,
  });

  const signOutMutation = useMutation({
    mutationFn: authApi.signOut,
    // Clear regardless of what the server said. The user asked to be signed out.
    onSettled: () => {
      setCurrentOrganizationId(null);
      queryClient.clear();
    },
  });

  const signOut = React.useCallback(async () => {
    await signOutMutation.mutateAsync().catch(() => undefined);
  }, [signOutMutation]);

  const switchOrganization = React.useCallback(
    (organizationId: string) => {
      setCurrentOrganizationId(organizationId);
      // Every cached response was scoped to the previous organization.
      queryClient.clear();
    },
    [queryClient],
  );

  // The API client has no React context, so it calls back here when a request
  // comes back 401 and the cached user is therefore stale.
  React.useEffect(() => {
    setUnauthorizedHandler(() => {
      setCurrentOrganizationId(null);
      queryClient.setQueryData(USER_QUERY_KEY, null);
    });
  }, [queryClient]);

  const value = React.useMemo<SessionContextValue>(
    () => ({
      user: user ?? null,
      isLoading,
      isResolved: isFetched,
      signOut,
      switchOrganization,
      refresh: refetch,
    }),
    [user, isLoading, isFetched, signOut, switchOrganization, refetch],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
};

export const useSession = (): SessionContextValue => {
  const context = React.useContext(SessionContext);

  if (!context) {
    throw new Error('useSession must be used inside a SessionProvider');
  }

  return context;
};
